import { FormGroup, Grid } from '@mui/material'
import omit from 'lodash/omit'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums'
import { CatalogProduct } from '../types'
import { ElectricalCabinetCatalogProduct } from '../types/CatalogProduct'
import { ElectricalCabinetCatalogProductCustomDataInput } from '../types/CatalogProductInput'
import CatalogProductForm from './CatalogProductForm'
import NominalCurrentSelect from './NominalCurrentSelect'
import YesNoSelect from './YesNoSelect'
// import ClampRatioField from './manufacturing/ClampRatioField'
import InputCablesSelect from './InputCablesSelect'
import SinglePhaseToggleButtonGroup from './SinglePhaseToggleButtonGroup'
import { getClampRatioForNominalCurrent } from '../utils/powerSupply'

export interface ElectricalCabinetCatalogProductFormProps {
  catalogProduct?: ElectricalCabinetCatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

export default function ElectricalCabinetCatalogProductForm({
  catalogProduct,
  ...props
}: ElectricalCabinetCatalogProductFormProps) {
  return (
    <CatalogProductForm
      {...props}
      type={CatalogProductType.ELECTRICAL_CABINET}
      catalogProduct={catalogProduct}
    />
  )
}

export function ElectricalCabinetCatalogProductCustomDataFields({
  value,
  disabled = false,
  isError,
  getHelperText,
  onChange
}: {
  value: ElectricalCabinetCatalogProductCustomDataInput
  catalogProduct?: CatalogProduct
  disabled?: boolean
  isError?: (field: string) => boolean
  getHelperText?: (field: string) => string | undefined
  onChange: (value: ElectricalCabinetCatalogProductCustomDataInput) => void
}) {
  const { t } = useTranslation('catalogProduct')

  function handleNominalCurrentChange(nominalCurrent: number) {
    onChange({
      ...value,
      nominalCurrent,
      fuse: nominalCurrent,
      clampRatio: getClampRatioForNominalCurrent(nominalCurrent)
    })
  }

  // function handleClampRatioChange(clampRatio: number | null) {
  //   onChange({ ...value, clampRatio: clampRatio ?? undefined })
  // }

  function handleInputCablesChange(inputCables: number | undefined) {
    onChange(inputCables === undefined ? omit(value, 'inputCables') : { ...value, inputCables })
  }

  function handleSinglePhaseChange(singlePhase: 0 | 1 | null) {
    onChange(
      singlePhase === null
        ? omit(value, 'isSinglePhase')
        : { ...value, isSinglePhase: singlePhase === 1 }
    )
  }

  function handleIsMainDistributionAssemblyChange(isMainDistributionAssembly: boolean) {
    onChange({ ...value, isMainDistributionAssembly })
  }

  return (
    <FormGroup>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <NominalCurrentSelect
            value={value.nominalCurrent}
            error={isError?.('customData.nominalCurrent')}
            helperText={getHelperText?.('customData.nominalCurrent')}
            disabled={disabled}
            fullWidth
            onChange={handleNominalCurrentChange}
          />
        </Grid>

        {/*
        <Grid item xs={12}>
          <ClampRatioField
            value={value.clampRatio ?? null}
            error={isError?.('customData.clampRatio')}
            helperText={getHelperText?.('customData.clampRatio')}
            disabled={disabled}
            fullWidth
            allowEmpty
            onValueChange={handleClampRatioChange}
          />
        </Grid>
        */}

        <Grid item xs={12}>
          <InputCablesSelect
            value={value.inputCables}
            allowEmpty
            error={isError?.('customData.inputCables')}
            helperText={getHelperText?.('customData.inputCables')}
            disabled={disabled}
            fullWidth
            onChange={handleInputCablesChange}
          />
        </Grid>

        <Grid item xs={12}>
          <SinglePhaseToggleButtonGroup
            value={value.isSinglePhase === undefined ? undefined : value.isSinglePhase ? 1 : 0}
            disabled={disabled}
            fullWidth
            onChange={handleSinglePhaseChange}
          />
        </Grid>

        <Grid item xs={12}>
          <YesNoSelect
            label={t('isMainDistributionAssembly')}
            value={value.isMainDistributionAssembly}
            error={isError?.('customData.isMainDistributionAssembly')}
            helperText={getHelperText?.('customData.isMainDistributionAssembly')}
            disabled={disabled}
            variant="outlined"
            fullWidth
            onChange={handleIsMainDistributionAssemblyChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}
